import {
  Button,
  cn,
  Icon,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Switch,
} from "@qw/ui";
import { type FC, useMemo, useRef, useState } from "react";
import {
  createTemplateParamDefinition,
  getDeclaredTemplateParams,
  getSaveBlockingTemplateAuthoringWarnings,
  getTemplateAuthoringValidationMessage,
  isTemplateParamType,
  TEMPLATE_PARAM_TYPES,
  type TemplateAuthoringValidationWarning,
  type TemplateParamDefinition,
  type TemplateParamType,
} from "./playbook-template-params";
import { isValidTemplateSlotParamName } from "./playbook-workflow-extensions/editor-extension-template-slot";

interface PlaybookTemplateParamDeclarerProps {
  disabled?: boolean;
  onChange: (params: TemplateParamDefinition[]) => void;
  params: TemplateParamDefinition[];
}

interface TemplateAuthoringValidationSummaryProps {
  warnings: TemplateAuthoringValidationWarning[];
}

interface TemplateParamRow {
  definition: TemplateParamDefinition;
  id: string;
}

const TEMPLATE_PARAM_TYPE_LABELS: Record<TemplateParamType, string> = {
  string: "String",
  text: "Text",
  boolean: "Boolean",
  enum: "Enum",
};

const isDuplicateParamName = (params: TemplateParamDefinition[], index: number, name: string): boolean =>
  params.some((param, paramIndex) => paramIndex !== index && param.name === name);

export const PlaybookTemplateParamDeclarer: FC<PlaybookTemplateParamDeclarerProps> = ({
  disabled,
  onChange,
  params,
}) => {
  const nextParamRowIdRef = useRef(0);
  const paramRowIdsRef = useRef<string[]>([]);
  const createTemplateParamRowId = () => {
    nextParamRowIdRef.current += 1;

    return `template-param-row-${nextParamRowIdRef.current}`;
  };
  while (paramRowIdsRef.current.length < params.length) {
    paramRowIdsRef.current.push(createTemplateParamRowId());
  }
  if (paramRowIdsRef.current.length > params.length) {
    paramRowIdsRef.current.length = params.length;
  }

  const paramRows: TemplateParamRow[] = params.map((param, index) => ({
    definition: param,
    id: paramRowIdsRef.current[index] ?? createTemplateParamRowId(),
  }));
  const declaredParams = useMemo(() => getDeclaredTemplateParams(params), [params]);

  const handleAddParam = () => {
    paramRowIdsRef.current = [...paramRowIdsRef.current, createTemplateParamRowId()];
    onChange([...params, createTemplateParamDefinition(params)]);
  };

  const handleRemoveParam = (index: number) => {
    paramRowIdsRef.current = paramRowIdsRef.current.filter((_, paramIndex) => paramIndex !== index);
    onChange(params.filter((_, paramIndex) => paramIndex !== index));
  };

  const handleNameChange = (index: number, rawName: string) => {
    const name = rawName.trim();
    if (!isValidTemplateSlotParamName(name) || isDuplicateParamName(params, index, name)) {
      return;
    }

    onChange(params.map((param, paramIndex) => (paramIndex === index ? { ...param, name } : param)));
  };

  const handleTypeChange = (index: number, value: string) => {
    if (!isTemplateParamType(value)) {
      return;
    }

    onChange(
      params.map((param, paramIndex) => {
        if (paramIndex !== index) {
          return param;
        }

        if (value === "enum") {
          return { ...param, type: value, enumValues: param.enumValues?.length ? param.enumValues : ["Option"] };
        }

        return {
          name: param.name,
          type: value,
          required: param.required,
          ...(param.label ? { label: param.label } : {}),
        };
      })
    );
  };

  const handleRequiredChange = (index: number, required: boolean) => {
    onChange(params.map((param, paramIndex) => (paramIndex === index ? { ...param, required } : param)));
  };

  const handleAddEnumValue = (index: number) => {
    onChange(
      params.map((param, paramIndex) =>
        paramIndex === index ? { ...param, enumValues: [...(param.enumValues ?? []), "Option"] } : param
      )
    );
  };

  const handleEnumValueChange = (index: number, enumValueIndex: number, value: string) => {
    onChange(
      params.map((param, paramIndex) =>
        paramIndex === index
          ? {
              ...param,
              enumValues: (param.enumValues ?? []).map((enumValue, currentEnumValueIndex) =>
                currentEnumValueIndex === enumValueIndex ? value : enumValue
              ),
            }
          : param
      )
    );
  };

  const handleRemoveEnumValue = (index: number, enumValueIndex: number) => {
    onChange(
      params.map((param, paramIndex) =>
        paramIndex === index
          ? {
              ...param,
              enumValues: (param.enumValues ?? []).filter((_, currentEnumValueIndex) => {
                return currentEnumValueIndex !== enumValueIndex;
              }),
            }
          : param
      )
    );
  };

  // Start collapsed when the template already declares fields — that's the "coming
  // back to edit the copy" case, where the body should be on screen. A template with
  // no fields yet opens expanded, since declaring one is the next thing to do.
  const [isExpanded, setIsExpanded] = useState(params.length === 0);

  const fieldSummary = useMemo(() => {
    if (paramRows.length === 0) {
      return "No fields declared.";
    }

    const names = paramRows.map(({ definition }, index) => definition.name || `field ${index + 1}`);
    const shown = names.slice(0, 3).join(", ");
    const remaining = names.length - 3;

    return `${names.length} ${names.length === 1 ? "field" : "fields"} · ${shown}${remaining > 0 ? ` +${remaining} more` : ""}`;
  }, [paramRows]);

  return (
    <section className="max-w-4xl rounded-md border bg-background" data-testid="template-param-declarer">
      <div className="flex items-start justify-between gap-3 px-3 py-2">
        {/* The email body is the artifact being authored, so it should be what you
            see on open. Field declarations matter while you're setting them up and
            then mostly stay put, so once there are any they collapse to a summary. */}
        <button
          type="button"
          onClick={() => setIsExpanded((expanded) => !expanded)}
          aria-expanded={isExpanded}
          className="flex min-w-0 items-start gap-1.5 text-left cursor-pointer"
        >
          <Icon
            icon="keyboard_arrow_down"
            className={cn("mt-0.5 h-4 w-4 shrink-0 transition-transform", { "-rotate-90": !isExpanded })}
          />
          <span className="min-w-0">
            <span className="block text-sm font-semibold">Template fields</span>
            <span className="mt-0.5 block truncate text-xs text-muted-foreground">
              {isExpanded ? "Declare the values this template can receive when it is filled." : fieldSummary}
            </span>
          </span>
        </button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            setIsExpanded(true);
            handleAddParam();
          }}
          disabled={disabled}
        >
          <Icon icon="add" />
          Add field
        </Button>
      </div>

      {isExpanded && paramRows.length > 0 && <Separator orientation="horizontal" />}

      {!isExpanded ? null : paramRows.length === 0 ? (
        <div className="px-3 pb-3 text-xs text-muted-foreground">No fields declared.</div>
      ) : (
        <div className="divide-y">
          {paramRows.map(({ definition: param, id }, index) => {
            const rowLabel = param.name || `field ${index + 1}`;

            return (
              <div key={id} className="space-y-2 px-3 py-3">
                <div className="grid grid-cols-[minmax(9rem,1fr)_8rem_auto_auto] items-end gap-2">
                  <div className="space-y-1.5">
                    <Label htmlFor={`template-param-name-${index}`} className="text-xs text-muted-foreground">
                      Name
                    </Label>
                    <Input
                      id={`template-param-name-${index}`}
                      aria-label={`Name for ${rowLabel}`}
                      value={param.name}
                      disabled={disabled}
                      onChange={(event) => handleNameChange(index, event.target.value)}
                      className={cn("h-9 font-mono", {
                        "border-destructive": !declaredParams.some(
                          (declaredParam) => declaredParam.name === param.name
                        ),
                      })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <Select value={param.type} onValueChange={(value) => handleTypeChange(index, value)}>
                      <SelectTrigger
                        aria-label={`Type for ${rowLabel}`}
                        disabled={disabled}
                        size="sm"
                        className="bg-background"
                      >
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_PARAM_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {TEMPLATE_PARAM_TYPE_LABELS[type]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex h-9 items-center gap-2 pb-px">
                    <Switch
                      aria-label={`Required for ${rowLabel}`}
                      checked={param.required}
                      disabled={disabled}
                      onCheckedChange={(required) => handleRequiredChange(index, required)}
                    />
                    <Label className="text-xs text-muted-foreground">Required</Label>
                  </div>

                  <Button
                    type="button"
                    display="icon"
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${rowLabel}`}
                    onClick={() => handleRemoveParam(index)}
                    disabled={disabled}
                  >
                    <Icon icon="delete" />
                  </Button>
                </div>

                {param.type === "enum" && (
                  <div className="ml-0 grid gap-2 rounded-sm bg-muted/40 p-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-xs text-muted-foreground">Enum values</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleAddEnumValue(index)}
                        disabled={disabled}
                      >
                        <Icon icon="add" />
                        Add value
                      </Button>
                    </div>

                    {(param.enumValues ?? []).map((enumValue, enumValueIndex) => (
                      <div key={`${param.name}-enum-${enumValueIndex}`} className="flex items-center gap-2">
                        <Input
                          aria-label={`Enum value ${enumValueIndex + 1} for ${rowLabel}`}
                          value={enumValue}
                          disabled={disabled}
                          onChange={(event) => handleEnumValueChange(index, enumValueIndex, event.target.value)}
                          className="h-8"
                        />
                        <Button
                          type="button"
                          display="icon"
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove enum value ${enumValueIndex + 1} for ${rowLabel}`}
                          onClick={() => handleRemoveEnumValue(index, enumValueIndex)}
                          disabled={disabled || (param.enumValues ?? []).length <= 1}
                        >
                          <Icon icon="close" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export const TemplateAuthoringValidationSummary: FC<TemplateAuthoringValidationSummaryProps> = ({ warnings }) => {
  if (warnings.length === 0) {
    return null;
  }

  const hasSaveBlockingWarnings = getSaveBlockingTemplateAuthoringWarnings(warnings).length > 0;

  return (
    <section
      className={cn(
        "max-w-4xl rounded-md border px-3 py-2.5",
        // Theme tokens, not the raw amber ramp: the warning state used to hard-code
        // bg-amber-50 / text-amber-950 (light-mode values), which lands as a
        // near-white block in the dark editor. The two states are also weighted
        // differently on purpose — blocking a save should read louder than a hint
        // you can ignore, so only it carries coloured text.
        hasSaveBlockingWarnings ? "border-destructive/40 bg-destructive/10" : "border-warning/30 bg-warning/5"
      )}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <Icon
          icon="warning"
          className={cn("text-base", hasSaveBlockingWarnings ? "text-destructive" : "text-warning")}
        />
        <span className={hasSaveBlockingWarnings ? "text-destructive" : "text-foreground"}>
          {hasSaveBlockingWarnings ? "Template save blocked" : "Template warnings"}
        </span>
      </div>
      {hasSaveBlockingWarnings && (
        <p className="mt-1 text-xs text-muted-foreground">Fix these field issues before saving.</p>
      )}
      <ul className="mt-1.5 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
        {warnings.map((warning) => (
          <li key={`${warning.code}-${warning.param}`}>{getTemplateAuthoringValidationMessage(warning)}</li>
        ))}
      </ul>
    </section>
  );
};
